import {defineStore} from 'pinia'
import {$$, $computed, $ref} from '@vue-macros/reactivity-transform/macros'
import {useWorkspaceStore} from '@toolify/client/src/stores/WorkspaceStore/useWorkspaceStore'
import {IResourceSpaceResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IResourceSpaceResponseInstance'
import {useResourceSpaceApiStore} from '@toolify/client/src/stores/api/ResourceSpaceApiStore/useResourceGroupApiStore'
import {useSocketListener} from '@toolify/client/src/composables/useSocketListener'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {
  IResourceSpaceCreatedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IResourceSpaceCreatedEmitPayload'
import {
  IResourceSpaceUpdatedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IResourceSpaceUpdatedEmitPayload'
import {
  IResourceSpaceDeletedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IResourceSpaceDeletedEmitPayload'

export const useSpaceStore = defineStore('space', () => {
  const workspaceStore = useWorkspaceStore()
  const resourceSpaceApiStore = useResourceSpaceApiStore()

  let currentSpaceIdByWorkspaceIdMap = $ref<Map<string, string>>(new Map())
  let resourceSpacesByWorkspaceIdMap = $ref<Map<string, IResourceSpaceResponseInstance[]>>(new Map())
  const latestReorderOperationIdByWorkspaceIdMap = new Map<string, number>()
  let lastReorderOperationId = 0

  const currentSpaceId = $computed(() => {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if (!workspaceId) {
      return undefined
    }
    return currentSpaceIdByWorkspaceIdMap.get(workspaceId)
  })

  const resourceSpaces = $computed(() => {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if (!workspaceId) {
      return []
    }
    const spaces = resourceSpacesByWorkspaceIdMap?.get(workspaceId)
    if (!spaces) {
      return []
    }
    return [...spaces].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  })

  const currentSpace = $computed(() => {
    if (!currentSpaceId) {
      return resourceSpaces?.[0] || null
    }
    return resourceSpaces.find(space => space.id === currentSpaceId) || resourceSpaces?.[0] || null
  })

  function setCurrentSpaceId(workspaceId: string, spaceId: string) {
    const newMap = new Map(currentSpaceIdByWorkspaceIdMap)
    newMap.set(workspaceId, spaceId)
    currentSpaceIdByWorkspaceIdMap = newMap
  }

  function setResourceSpacesByWorkspaceId(workspaceId: string, spaces: IResourceSpaceResponseInstance[]) {
    const newMap = new Map(resourceSpacesByWorkspaceIdMap)
    newMap.set(workspaceId, spaces)
    resourceSpacesByWorkspaceIdMap = newMap
  }

  function setResourceSpaces(spaces: IResourceSpaceResponseInstance[]) {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if (!workspaceId) return
    setResourceSpacesByWorkspaceId(workspaceId, spaces)
  }

  function hasResourceSpacesLoaded(workspaceId: string) {
    return resourceSpacesByWorkspaceIdMap.has(workspaceId)
  }

  async function fetchResourceSpaces(workspaceId: string) {
    const res = await resourceSpaceApiStore.getResourceSpaces(workspaceId)
    setResourceSpacesByWorkspaceId(workspaceId, res.resourceSpaces)
  }

  async function moveResourceSpace(oldIndex: number, newIndex: number) {
    const workspaceId = workspaceStore.currentWorkspace?.id
    const movedSpace = resourceSpaces[oldIndex]
    if (!workspaceId || !movedSpace || oldIndex === newIndex) {
      return
    }
    const reorderOperationId = lastReorderOperationId + 1
    lastReorderOperationId = reorderOperationId
    latestReorderOperationIdByWorkspaceIdMap.set(workspaceId, reorderOperationId)
    const reorderedSpaces = [...resourceSpaces]
    reorderedSpaces.splice(oldIndex, 1)
    reorderedSpaces.splice(newIndex, 0, movedSpace)
    setResourceSpacesByWorkspaceId(workspaceId, reorderedSpaces.map((space, position) => ({
      ...space,
      position,
    })))
    const isStillLatestReorder = () => latestReorderOperationIdByWorkspaceIdMap.get(workspaceId) === reorderOperationId
    try {
      const res = await resourceSpaceApiStore.updateResourceSpacePosition(movedSpace.id, newIndex)
      if (isStillLatestReorder()) {
        setResourceSpacesByWorkspaceId(workspaceId, res.resourceSpaces)
      }
    } catch {
      if (isStillLatestReorder()) {
        await fetchResourceSpaces(workspaceId)
      }
    }
  }

  const alwaysActive = {
    lifecycleFn() {
      return true
    },
  }

  useSocketListener<IResourceSpaceCreatedEmitPayload>(GlobalSocketEventType.ResourceSpaceCreated, payload => {
    const {workspaceId, resourceSpace} = payload
    if (!resourceSpacesByWorkspaceIdMap.has(workspaceId)) {
      return
    }
    const existingSpaces = resourceSpacesByWorkspaceIdMap.get(workspaceId) || []
    if (existingSpaces.some(space => space.id === resourceSpace.id)) {
      return
    }
    setResourceSpacesByWorkspaceId(workspaceId, [...existingSpaces, resourceSpace])
  }, alwaysActive)

  useSocketListener<IResourceSpaceUpdatedEmitPayload>(GlobalSocketEventType.ResourceSpaceUpdated, payload => {
    const {workspaceId, resourceSpace} = payload
    if (!resourceSpacesByWorkspaceIdMap.has(workspaceId)) {
      return
    }
    const existingSpaces = resourceSpacesByWorkspaceIdMap.get(workspaceId) || []
    setResourceSpacesByWorkspaceId(workspaceId, existingSpaces.map(space => {
      if (space.id !== resourceSpace.id) {
        return space
      }
      return {
        ...space,
        ...resourceSpace,
      }
    }))
  }, alwaysActive)

  useSocketListener<IResourceSpaceDeletedEmitPayload>(GlobalSocketEventType.ResourceSpaceDeleted, payload => {
    const {workspaceId, resourceSpaceId} = payload
    if (!resourceSpacesByWorkspaceIdMap.has(workspaceId)) {
      return
    }
    const existingSpaces = resourceSpacesByWorkspaceIdMap.get(workspaceId) || []
    setResourceSpacesByWorkspaceId(workspaceId, existingSpaces.filter(space => space.id !== resourceSpaceId))
  }, alwaysActive)

  return $$({
    currentSpaceId,
    currentSpace,
    setCurrentSpaceId,
    resourceSpaces,
    setResourceSpaces,
    setResourceSpacesByWorkspaceId,
    hasResourceSpacesLoaded,
    fetchResourceSpaces,
    moveResourceSpace,
  })
})
